import React, { useEffect, useMemo, useState } from 'react';
import { useStore } from 'store/store';
import { ACCESS_BASE_URL } from 'App';

import {
  useUtxCardGetCardAccounts,
  GetCardAccountsResponseDataItem,
  GetCardAccountsResponseDataItemCardsItem,
  useUtxUsersUploadDocument,
  UtxUsersUploadDocumentBody,
  UtxUsersUploadDocumentMutationResult,
  UtxUsersUploadDocumentMutationError
} from '@utx/utx-client';
import { PageToken, useCreateCardAccount, useGetAccountsCreditSummary, useListAccounts, useListCountries } from '@utx/utx-access-client';
import { CardAccountType } from '../enum';

export type AccountItem = GetCardAccountsResponseDataItem;

export type CardItem = GetCardAccountsResponseDataItemCardsItem;

export interface useCardAccountsProps {
  current?: number;
  pageSize?: number;
  search?: string;
}

export const useCardAccounts = (props: useCardAccountsProps) => {
  const { current = 1, pageSize = 10, search } = props;
  const store = useStore();

  const [pagination, setPagination] = useState({
    total: 0,
    current: current,
    perPage: pageSize,
    totalPages: 1
  });
  const [accounts, setAccounts] = useState<AccountItem[]>([]);
  const [enabled, setEnabled] = useState<boolean>(false);
  setTimeout(() => {
    setEnabled(store.authToken ? true : false);
  }, 100);

  const queryAccounts = useUtxCardGetCardAccounts(
    {
      current: current,
      pageSize: pageSize,
      search: search
    },
    {
      query: {
        queryKey: ['queryAccounts', store.authToken, current, pageSize, search],
        retry: false,
        enabled,
        keepPreviousData: false
      }
    }
  );

  useEffect(() => {
    if (queryAccounts.data?.data) {
      const total = queryAccounts.data.data.total || queryAccounts.data.data.data.length || pagination.total;
      const currentPage = queryAccounts.data.data.current || current;
      const perPage = queryAccounts.data.data.perPage || pageSize;
      const totalPages = queryAccounts.data.data.totalPages || Math.ceil(total / perPage);
      setPagination({
        total,
        current: currentPage,
        perPage,
        totalPages
      });
      setAccounts(queryAccounts.data.data.data);
    }
  }, [queryAccounts.data?.data]);

  const createCardAccountMutate = useCreateCardAccount({ axios: { baseURL: ACCESS_BASE_URL } });
  const createCardAccount = (data: any, onSuccess?: (data: any) => void, onError?: (error: any) => void) => {
    createCardAccountMutate.mutate(
      {
        data
      },
      {
        onSuccess,
        onError
      }
    );
  };

  return {
    pagination,
    accounts,
    isLoading: queryAccounts.isLoading,
    reload: () => queryAccounts.refetch(),
    createCardAccount
  };
};

export interface useAccountsProps {
  name?: string;
  search?: string;
  pageSize?: number;
}

export const useAccounts = (props: useAccountsProps) => {
  const { name, search, pageSize = 10 } = props;
  const store = useStore();

  const [currentPageToken, setCurrentPageToken] = useState<PageToken>();
  const [pageToken, setPageToken] = useState<{ nextToken?: PageToken; preToken?: PageToken }>({});
  const [enabled, setEnabled] = useState<boolean>(false);
  setTimeout(() => {
    setEnabled(store.authToken ? true : false);
  }, 100);

  const queryAccounts = useListAccounts(
    {
      name: name,
      search: search,
      pageSize: pageSize,
      pageToken: currentPageToken
    },
    {
      axios: {
        baseURL: ACCESS_BASE_URL
      },
      query: {
        queryKey: ['queryAccounts', store.authToken, currentPageToken, pageSize, name, search],
        retry: false,
        enabled,
        keepPreviousData: true
      }
    }
  );

  useEffect(() => {
    setPageToken({ nextToken: queryAccounts.data?.data.nextPageToken, preToken: queryAccounts.data?.data.prevPageToken });
  }, [queryAccounts.data?.data]);

  return {
    accounts: queryAccounts.data?.data.accounts || [],
    isLoading: queryAccounts.isLoading,
    nextToken: pageToken.nextToken,
    preToken: pageToken.preToken,
    prev: () => setCurrentPageToken(pageToken.preToken),
    next: () => setCurrentPageToken(pageToken.nextToken),
    refetch: () => queryAccounts.refetch()
  };
};

export interface useCountriesProps {}

export const useCountries = (props: useCountriesProps) => {
  const {} = props;
  const store = useStore();

  const [enabled, setEnabled] = useState<boolean>(false);
  setTimeout(() => {
    setEnabled(store.authToken ? true : false);
  }, 100);

  const countries = useListCountries({
    axios: {
      baseURL: ACCESS_BASE_URL
    },
    query: {
      queryKey: ['queryCountries', store.authToken],
      retry: false,
      enabled,
      keepPreviousData: true
    }
  });

  return {
    countries
  };
};

export interface useUploadDocumentProps {}

export const useUploadDocument = (props: useUploadDocumentProps) => {
  const {} = props;
  const uploadDocumentQuery = useUtxUsersUploadDocument();
  const postUploadDocument = (
    request: UtxUsersUploadDocumentBody,
    onSuccess: (data: UtxUsersUploadDocumentMutationResult) => void,
    onError: (error: UtxUsersUploadDocumentMutationError) => void
  ) => {
    uploadDocumentQuery.mutate(
      {
        data: request
      },
      {
        onSuccess,
        onError
      }
    );
  };

  return {
    postUploadDocument
  };
};

export interface useAccountCreditSummaryProps {
  walletId?: string;
  currency?: string;
  accountType?: string;
}

export const useAccountCreditSummary = ({ walletId, currency, accountType }: useAccountCreditSummaryProps) => {
  const store = useStore();

  const [enabled, setEnabled] = useState<boolean>(false);
  setTimeout(() => {
    setEnabled(store.authToken && walletId && currency && (accountType === CardAccountType.FIXED_LIMIT) ? true : false);
  }, 100);

  const queryCreditSummary = useGetAccountsCreditSummary(
    {
      accountId: walletId!,
      currency: currency!
    },
    {
      axios: {
        baseURL: ACCESS_BASE_URL
      },
      query: {
        queryKey: ['queryCreditSummary', store.authToken, walletId, currency],
        retry: false,
        enabled,
        keepPreviousData: true
      }
    }
  );

  return {
    creditSummary: queryCreditSummary.data?.data,
    isLoadingCreditSummary: queryCreditSummary.isLoading,
    refetch: () => queryCreditSummary.refetch(),
  }
}
